import { assert, TimeDistanceMode } from '@fe/core';
import { FeMapPlugin } from '@fe/maplibre-extensions';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import * as ai from './ai-routes-helper';
import * as e from './etops-helper';
import * as pl from './flight-plan-routes-helper';
import * as fl from './flown-route-helper';
import * as imp from './impacts-helper';
import {
    EtopsColorSettings,
    FlightPlanRoute,
    FlightPlanSettings,
    FlownRoute,
    FlownRouteColorSettings,
    FlownRouteParams,
    FlownRouteSettings,
    RouteAnalytics,
    RouteFlownPoint
} from './types';

export const DEFAULT_ROUTE_WIDTH = 3;
export const DEFAULT_ROUTE_FLOWN_COLOR = '#f0f';

export interface FlightRoutes {
    planned: FlightPlanRoute | null;
    atc: FlightPlanRoute | null;
    flown: FlownRoute | null;
    destination: {
        name: string;
        position: [number, number];
    } | null;
}

export interface FeatureIds {
    flightPlan: string[];
    flown: string[];
    impacts: string[];
    etops: string[];
}

export class RoutesLayer implements FeMapPlugin {
    private map: MaplibreMap | null = null;
    private readonly featureIds = new Map<number, FeatureIds>();
    private readonly flown: fl.FlownRouteHelper = new fl.FlownRouteHelper(this.featureIds, 'fe-routes-slot');
    private readonly planned: pl.FlightPlanRoutesHelper = new pl.FlightPlanRoutesHelper(this.featureIds, 'fe-routes-slot');
    private readonly impacts: imp.ImpactsHelper = new imp.ImpactsHelper(this.featureIds, this.planned, 'fe-routes-slot');
    private readonly etops: e.EtopsHelper = new e.EtopsHelper(this.featureIds, 'fe-routes-slot');
    private readonly ai: ai.AIRoutesHelper = new ai.AIRoutesHelper(pl.LINE);

    get flightPlanSettings(): FlightPlanSettings | null {
        return this.planned.settings;
    }

    set flightPlanSettings(value: FlightPlanSettings | null) {
        this.planned.settings = value;
        this.etops.settings = value;
        this.ai.settings = value;
        this.impacts.showHide();
    }

    get flownRouteSettings(): FlownRouteSettings | null {
        return this.flown.settings;
    }

    set flownRouteSettings(value: FlownRouteSettings | null) {
        this.flown.settings = value;
    }

    get flownRouteParams() {
        return this.flown.params;
    }

    set flownRouteParams(value: FlownRouteParams) {
        this.flown.params = value;
    }

    get shouldSmoothenRouteFlown(): boolean {
        return this.flown.shouldSmoothen;
    }

    set shouldSmoothenRouteFlown(value: boolean) {
        this.flown.shouldSmoothen = value;
    }

    get showProjectedPointsRouteFlown(): boolean {
        return this.flown.showProjectedPoints;
    }

    set showProjectedPointsRouteFlown(value: boolean) {
        this.flown.showProjectedPoints = value;
    }

    get routeFlownColorSettings(): FlownRouteColorSettings | null {
        return this.flown.colorSettings;
    }

    set routeFlownColorSettings(value: FlownRouteColorSettings) {
        this.flown.colorSettings = value;
    }

    get etopsColorSettings(): EtopsColorSettings | null {
        return this.etops.colorSettings;
    }

    set etopsColorSettings(value: EtopsColorSettings | null) {
        this.etops.colorSettings = value;
    }

    get showImpacts(): boolean {
        return this.impacts.show;
    }

    set showImpacts(value: boolean) {
        this.impacts.show = value;
    }

    get timeDistanceMode(): TimeDistanceMode {
        return this.planned.timeDistanceMode;
    }

    set timeDistanceMode(value: TimeDistanceMode) {
        this.planned.timeDistanceMode = value;
    }

    getFlights(): number[] {
        return this.featureIds.keys().toArray();
    }

    upsertFlight(id: number, routes: FlightRoutes) {
        if (!this.map) {
            return;
        }

        if (!this.featureIds.has(id)) {
            this.featureIds.set(id, { flown: [], flightPlan: [], impacts: [], etops: [] });
        }

        // Order matters
        this.upsertFlownRoutes(id, routes);
        this.upsertFlightPlanRoutes(id, routes);
        this.upsertImpacts(id, routes);
        this.upsertEtops(id, routes);
    }

    addRouteAi(analytics: RouteAnalytics | null) {
        if (!this.map) {
            return;
        }

        this.ai.upsert(analytics);
    }

    addFlownRoutePoint(flightId: number, point: RouteFlownPoint, atIndex: number) {
        if (!this.map) {
            return;
        }

        if (this.featureIds.has(flightId)) {
            this.flown.addRoutePoint(flightId, point, atIndex);
            const lastPoint = this.flown.getLastFlownPoint(flightId);
            if (lastPoint) {
                this.planned.lastFlownPointChanged(flightId, lastPoint);
            }
        }
    }

    removeAll() {
        if (!this.map) {
            return;
        }

        this.featureIds.clear();
        this.impacts.removeAll();
        this.planned.removeAll();
        this.flown.removeAll();
        this.etops.removeAll();
        this.ai.removeAll();
    }

    removeFlight(id: number, options?: { keep: { flown: boolean; flightPlan: boolean } }) {
        if (!this.map) {
            return;
        }
        if (!this.featureIds.has(id)) {
            return;
        }

        if (!options?.keep.flightPlan) {
            this.planned.remove(id);
            this.impacts.remove(id);
            this.etops.remove(id);
            this.ai.remove();
        }

        if (!options?.keep.flown) {
            this.flown.remove(id);
        }

        if (!options?.keep.flown && !options?.keep.flightPlan) {
            this.featureIds.delete(id);
        }
    }

    onAdd(map: MaplibreMap) {
        assert(!this.map);

        this.map = map;
        this.impacts.addToMap(map);
        this.planned.addToMap(map);
        this.etops.addToMap(map);
        this.flown.addToMap(map);
        this.ai.addToMap(map);
    }

    onRemove() {
        assert(this.map);
        this.featureIds.clear();
        this.planned.removeFromMap();
        this.flown.removeFromMap();
        this.impacts.removeFromMap();
        this.etops.removeFromMap();
        this.ai.removeFromMap();

        this.map = null;
    }

    private upsertFlightPlanRoutes(flightId: number, routes: FlightRoutes) {
        const flightFeaturesIds = this.featureIds.get(flightId) as FeatureIds;
        const flightInfo = this.flown.getLastFlownPoint(flightId);
        const features = this.planned.upsert(flightId, routes, flightInfo);
        flightFeaturesIds.flightPlan = features.map((x) => x.id as string);
    }

    private upsertFlownRoutes(flightId: number, routes: FlightRoutes) {
        if (!routes.flown) {
            return;
        }
        const features = this.flown.upsert(flightId, [...routes.flown]); // copy the array in case it is immutable
        const flightFeaturesIds = this.featureIds.get(flightId) as FeatureIds;
        flightFeaturesIds.flown = features.map((x) => x.id as string);
    }

    private upsertEtops(flightId: number, routes: FlightRoutes) {
        const features = this.etops.upsert(flightId, routes);
        const flightFeaturesIds = this.featureIds.get(flightId) as FeatureIds;
        flightFeaturesIds.etops = features.map((x) => x.id as string);
    }

    private upsertImpacts(flightId: number, routes: FlightRoutes) {
        if (!this.map) {
            return;
        }

        const flightFeaturesIds = this.featureIds.get(flightId);
        if (!flightFeaturesIds) {
            return;
        }

        const source = this.map!.getSource(imp.SOURCE) as GeoJSONSource;
        assert(source);

        const features = [
            ...this.impacts.toRouteData(flightId, 'atc', routes.atc),
            ...this.impacts.toRouteData(flightId, 'planned', routes.planned)
        ];

        source.updateData({
            add: features,
            remove: flightFeaturesIds.impacts
        });

        flightFeaturesIds.impacts = features.map((x) => x.id as string);
    }
}
